import { createFileRoute, redirect, Outlet } from '@tanstack/react-router';
import { sessionQueryOptions } from '@entities/session';
import {
  planUsageQueryOptions,
  planUsageKeys,
  toPlanUsage,
  type PlanUsageResponse,
} from '@entities/plan-usage';
import { clearSession } from '@shared/auth';
import { AppShell } from '@app/shell';
// import { PageTransition } from '@ui';

export const Route = createFileRoute('/_authenticated')({
  // Guard is UX only — the BFF + backend independently authorize every request.
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(sessionQueryOptions).catch(() => null);
    if (!user) throw redirect({ to: '/login' });

    // Web access is Pro-only (mobile parity — FreeLimitType.webAccess). There's no
    // session-revocation/refresh endpoint, so a JWT issued while Pro stays valid after the
    // subscription lapses or is cancelled — without this, a user who went Pro → Free kept full
    // web access until they explicitly logged out and back in. Re-check the CURRENT plan (the
    // plan-usage endpoint, not the possibly-stale `plan` field from login) on every authenticated
    // navigation/refresh, since this beforeLoad re-runs then. A fetch failure fails OPEN (does not
    // block access) — an outage of this one endpoint must not lock out every Pro user.
    //
    // `ensureQueryData(planUsageQueryOptions)` resolves the RAW (pre-`select`) envelope, not the
    // mapped `PlanUsageModel` — `select` isn't applied by QueryClient methods outside `useQuery`,
    // only by the hook itself. Trusting that return value (even just via a type cast) previously
    // read `.isPro` off the raw envelope, which is always `undefined` there — so EVERY user,
    // Pro included, got treated as free and signed back out right after logging in. Ensuring the
    // fetch (for the cache) and then reading+mapping via `getQueryData` avoids the ambiguity: that
    // method is documented to always return the raw cached value, never a `select` result.
    await context.queryClient.ensureQueryData(planUsageQueryOptions).catch(() => undefined);
    const rawPlanUsage = context.queryClient.getQueryData<PlanUsageResponse>(
      planUsageKeys.detail(),
    );
    const planUsage = rawPlanUsage?.data ? toPlanUsage(rawPlanUsage.data) : null;
    if (planUsage && !planUsage.isPro) {
      clearSession();
      context.queryClient.clear();
      throw redirect({ to: '/login', search: { reason: 'pro_required' } });
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  return (
    <AppShell>
      {/* Wrap only the page body so the header + rail stay stable across navigations. */}
      {/* <PageTransition> */}
      <Outlet />
      {/* </PageTransition> */}
    </AppShell>
  );
}
